import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { get } from 'lodash';
import { connect } from 'react-redux';
import { setErrorFlashMessage, setSuccessFlashMessage } from '@src/redux/actions/flashMessages';
import { closeModal } from '@src/redux/actions/modal';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { SUBSCRIBE_ERROR_MESSAGES, CYCLE_LABELS, CYCLES } from '@src/constants';
import { useSetTotalSlotsCountMutation, useUpgradeSubscriptionDataQuery } from '@src/graphql/generated';
import { trackEvent } from '@src/metrics';
import { DetailsList, FooterInfoBlock, ModalInfo, ModalActions, ModalContainer, ModalTitle } from '../components';

const _OpeningsLimitReachedModal = ({ setErrorFlashMessage, setSuccessFlashMessage, closeModal, nextFunction }) => {
  const { loading, error, data } = useUpgradeSubscriptionDataQuery({
    fetchPolicy: 'network-only',
    onError: () => setErrorFlashMessage(),
  });

  const [setTotalSlotsCount, { loading: activateLoading }] = useSetTotalSlotsCountMutation({
    onCompleted: () => {
      setSuccessFlashMessage('Total slots limit has been successfully increased!');
    },
    onError: error => {
      if (error.graphQLErrors) {
        error.graphQLErrors.forEach(graphQLError => {
          const code = get(graphQLError, 'extensions.exception.code');

          if (code) {
            setErrorFlashMessage(SUBSCRIBE_ERROR_MESSAGES[code]);
          }
        });
      }
    },
  });

  if (loading) {
    return <ModalContainer isLoading />;
  }

  if (error) {
    closeModal();

    return null;
  }

  const {
    customer: {
      id: customerId,
      nextPaymentDate,
      planInfo: {
        currency: { symbol },
        slotPrice,
        openingSlots,
      },
    },
  } = data;

  return (
    <ModalContainer>
      <ModalTitle>Openings limit reached!</ModalTitle>
      <ModalInfo>In order to continue you’ll need to add another slot to increase your openings limit.</ModalInfo>
      <DetailsList
        details={[
          {
            name: 'Price increase',
            value: (
              <Fragment>
                <strong>
                  + {symbol}
                  {slotPrice}
                </strong>
                {CYCLE_LABELS[CYCLES.MONTHLY]}
              </Fragment>
            ),
          },
          {
            name: 'Next payment date',
            value: moment(nextPaymentDate).format('DD MMM YYYY'),
          },
        ]}
      />
      <ModalActions
        actions={[
          {
            isDisabled: activateLoading,
            name: 'Confirm',
            onClick: () => {
              trackEvent({
                context: MetricContext.BILLING,
                interaction: MetricAction.CHANGE_SLOTS_COUNT,
                payload: {
                  'Customer': customerId,
                  'New Slots count': openingSlots + 1,
                  'Old Slots count': openingSlots,
                },
              });

              setTotalSlotsCount({
                variables: {
                  totalSlotsCount: openingSlots + 1,
                },
              }).finally(() => {
                closeModal();
                nextFunction();
              });
            },
          },
          {
            buttonAppearance: 'secondary',
            isDisabled: activateLoading,
            name: 'Close',
            onClick: closeModal,
          },
        ]}
      />
      <FooterInfoBlock text="You will be charged the price difference for the remainder of your current billing period." />
    </ModalContainer>
  );
};

_OpeningsLimitReachedModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  nextFunction: PropTypes.func,
  setErrorFlashMessage: PropTypes.func.isRequired,
  setSuccessFlashMessage: PropTypes.func.isRequired,
};

export const OpeningsLimitReachedModal = connect(null, {
  closeModal,
  setErrorFlashMessage,
  setSuccessFlashMessage,
})(_OpeningsLimitReachedModal);
